import { useState, useSyncExternalStore } from 'react';
import { useSessionStorage as useSessionStorageHook } from 'usehooks-ts';
import type { SessionStorageKey } from './types';
import { serializeSessionStorageKey } from './versioned-key';

export interface UseSessionStorageParams<T> extends SessionStorageKey {
  readonly defaultValue: T;
  /**
   * Set to false when the value affects SSR-rendered markup — the hook then
   * returns `defaultValue` during hydration and reads storage after mount,
   * avoiding a server/client mismatch on hard loads.
   */
  readonly initializeWithValue?: boolean;
}

export function useSessionStorage<T>(
  args: UseSessionStorageParams<T>
): ReturnType<typeof useSessionStorageHook<T>> {
  const key = serializeSessionStorageKey(args);
  return useSessionStorageHook(key, args.defaultValue, {
    initializeWithValue: args.initializeWithValue,
  });
}

export interface WriteToSessionStorageParams<TValue> extends SessionStorageKey {
  readonly value: TValue;
}

export function writeToSessionStorage<TValue>(
  args: WriteToSessionStorageParams<TValue>
): void {
  if (
    typeof window === 'undefined' ||
    typeof window.sessionStorage === 'undefined'
  ) {
    return;
  }
  const key = serializeSessionStorageKey(args);
  try {
    sessionStorage.setItem(key, JSON.stringify(args.value));
    window.dispatchEvent(new StorageEvent('session-storage', { key }));
  } catch (error) {
    console.error('Error writing to session storage', error);
  }
}

export type RemoveFromSessionStorageParams = SessionStorageKey;

export function removeFromSessionStorage(
  args: RemoveFromSessionStorageParams
): void {
  if (
    typeof window === 'undefined' ||
    typeof window.sessionStorage === 'undefined'
  ) {
    return;
  }
  const key = serializeSessionStorageKey(args);
  try {
    sessionStorage.removeItem(key);
    window.dispatchEvent(new StorageEvent('session-storage', { key }));
  } catch (error) {
    console.error('Error removing from session storage', error);
  }
}

export interface ReadFromSessionStorageParams<
  TValue,
> extends SessionStorageKey {
  readonly defaultValue: TValue;
}

export function readFromSessionStorage<TValue>(
  args: ReadFromSessionStorageParams<TValue>
): TValue {
  if (
    typeof window === 'undefined' ||
    typeof window.sessionStorage === 'undefined'
  ) {
    return args.defaultValue;
  }
  const key = serializeSessionStorageKey(args);
  const value = sessionStorage.getItem(key);
  if (value === null) {
    return args.defaultValue;
  }

  try {
    return JSON.parse(value) as TValue;
  } catch {
    return args.defaultValue;
  }
}

export interface UseSessionStorageSnapshotParams<TValue>
  extends SessionStorageKey {
  readonly defaultValue: TValue;
}

export interface SessionStorageSnapshot<TValue> {
  readonly value: TValue;
  /** False on the server and during hydration, true once storage was read. */
  readonly restored: boolean;
  /** Remove the stored entry and reset `value` to `defaultValue`. */
  readonly clear: () => void;
}

/**
 * One-shot, SSR-safe sessionStorage restore — e.g. seeding a form draft that
 * survives reloads. Server render and hydration see `defaultValue`; right
 * after hydration the stored value is read once and the component re-renders
 * with it. The value is a mount-time snapshot: later writes to the key do
 * not update it (use `useSessionStorage` for a live binding).
 */
export function useSessionStorageSnapshot<TValue>(
  params: UseSessionStorageSnapshotParams<TValue>
): SessionStorageSnapshot<TValue> {
  // The store is created once so snapshots stay referentially stable and
  // `clear` can notify React; the params from the first render win (keys
  // are static in practice).
  const [store] = useState(() => {
    const serverSnapshot = { restored: false, value: params.defaultValue };
    let snapshot: { restored: boolean; value: TValue } | null = null;
    const listeners = new Set<() => void>();
    return {
      subscribe(listener: () => void) {
        listeners.add(listener);
        return () => listeners.delete(listener);
      },
      getSnapshot() {
        snapshot ??= { restored: true, value: readFromSessionStorage(params) };
        return snapshot;
      },
      getServerSnapshot: () => serverSnapshot,
      clear() {
        removeFromSessionStorage(params);
        snapshot = { restored: true, value: params.defaultValue };
        for (const listener of listeners) {
          listener();
        }
      },
    };
  });

  const { restored, value } = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getServerSnapshot
  );
  return { value, restored, clear: store.clear };
}
